import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

import { CaseStudy } from "@/types/content";

import { Button } from "@/components/ui/button";

import { motion } from "framer-motion";
import { fadeUp, viewportOnce } from "@/animations/motion";

import { fadeIn } from "@/animations/motion";

import { Breadcrumb } from "@/components/ui/breadcrumb";

import { AnimatedSection } from "@/components/common/AnimatedSection";

import { CaseStudyBreadcrumb } from "../casestudies/CaseStudyBreadcrumb";

import { CaseStudyHero } from "../casestudies/CaseStudyHero";

import { Section } from "../casestudies/Section";
import { BulletGrid } from "../casestudies/BulletGrid";
import { HighlightCard } from "../casestudies/HighlightCard";
import { MetricGrid } from "../casestudies/MetricGrid";

interface Props {
    caseStudy: CaseStudy;
}

export function CaseStudyTemplate({
    caseStudy,
}: Props) {
    return (
        <>
          <section className="relative overflow-hidden pt-28 lg:pt-32">

            <div className="mx-auto max-w-7xl px-6 py-12">

              <CaseStudyBreadcrumb
                  title={caseStudy.title}
              />

              <div className="mt-14 grid items-start gap-12 lg:grid-cols-[5fr_6fr]">
                <AnimatedSection>

                  <span
                      className="inline-flex rounded-full px-4 py-2 text-sm font-semibold"
                      style={{
                          backgroundColor: `${caseStudy.accent}15`,
                          color: caseStudy.accent,
                      }}
                  >
                      Concept Project · {caseStudy.category}
                  </span>

                  {/* <h1 className="mt-6 font-heading text-5xl font-bold leading-tight">
                      {caseStudy.title}
                  </h1>

                  <p className="mt-8 text-xl leading-9 text-muted-foreground">
                      {caseStudy.summary}
                  </p> */}
                  <h1 className="mt-6 font-heading text-4xl font-bold leading-tight">
                      {caseStudy.title}
                  </h1>

                  <div className="mt-5 p-3 rounded-2xl shadow-[0_50px_50px_-40px_rgba(15,23,42,0.35)]" style={{background: `${caseStudy.accent}12`,}}>
                    <p className="text-lg leading-8 text-muted-foreground">
                      {caseStudy.summary}
                    </p>
                  </div>

                  <h2 className="mt-8 font-heading text-3xl font-bold">
                      Project Overview
                  </h2>
                  <p className="mt-4 text-lg leading-8 text-muted-foreground">
                    Understanding the business context and the objectives behind this concept project.
                  </p>
                  <div className="mt-5 p-3 rounded-2xl shadow-[0_50px_50px_-40px_rgba(15,23,42,0.35)]" style={{background: `${caseStudy.accent}12`,}}>
                    <p className="text-lg leading-8 text-muted-foreground">
                      {caseStudy.overview}
                    </p>
                  </div>

                  <div className="mt-10 flex flex-wrap gap-3">

                    {caseStudy.tech.map((tech) => (

                        <span
                            key={tech}
                            className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium"
                        >
                            {tech}
                        </span>

                    ))}

                  </div>
                  <div className="mt-10 flex flex-wrap gap-4">

                    <Button asChild>

                        <Link to="/contact">

                            Discuss Your Project

                            <ArrowRight className="ml-2 h-4 w-4" />

                        </Link>

                    </Button>

                    <Button
                        variant="outline"
                        asChild
                    >

                        <Link to="/services">

                            Related Services

                        </Link>

                    </Button>

                  </div>

                </AnimatedSection>
                <AnimatedSection delay={0.15}>
                  <div className="mx-auto w-full max-w-[820px]">
                    <CaseStudyHero dashboard={caseStudy.dashboard} accent={caseStudy.accent} />
                  </div>
                </AnimatedSection>
              </div>
            </div>
          </section>
          {/* <Section
              id="overview"
              title="Project Overview"
              subtitle="Understanding the business context and the objectives behind this concept project."
          >
            <AnimatedSection>

                <div className="rounded-3xl border border-border bg-card p-8">

                    <p className="text-lg leading-8 text-muted-foreground">

                        {caseStudy.overview}

                    </p>

                </div>
            </AnimatedSection>
          </Section> */}
          <Section
            id="challenge"
            title="Business Challenges"
            subtitle="The operational problems this solution is designed to solve."
          >
            <AnimatedSection>
                <BulletGrid items={caseStudy.challenge} />
            </AnimatedSection>
          </Section>
          <Section
            id="approach"
            title="Implementation Approach"
            subtitle="A phased methodology designed to minimise risk and maximise adoption."
          >
            <AnimatedSection>

              <div className="grid gap-6 lg:grid-cols-2">

                {caseStudy.approach.map((phase, index) => (

                  <div key={phase.title} className="rounded-3xl border border-border bg-card p-8">
                    <div
                      className="mb-6 flex h-12 w-12 items-center justify-center rounded-full text-lg font-bold text-white"
                      style={{
                          backgroundColor: caseStudy.accent,
                      }}
                    >
                      {index + 1}
                    </div>

                    <h3 className="text-xl font-semibold">
                        {phase.title}
                    </h3>

                    <p className="mt-4 leading-7 text-muted-foreground">
                        {phase.description}
                    </p>
                  </div>

                ))}
              </div>
            </AnimatedSection>
          </Section>
          <Section
            id="solution"
            title="Solution Highlights"
            subtitle="Key capabilities delivered through the proposed platform."
          >
            <AnimatedSection>
              <div className="grid gap-5 md:grid-cols-2">

                  {caseStudy.solutionHighlights.map((item) => (

                      <div
                          key={item}
                          className="flex gap-4 rounded-2xl border border-border bg-card p-6"
                      >

                          <CheckCircle2
                              className="mt-1 h-6 w-6 flex-shrink-0"
                              style={{
                                  color: caseStudy.accent,
                              }}
                          />

                          <span className="leading-7">

                              {item}

                          </span>

                      </div>

                  ))}
              </div>
            </AnimatedSection>
          </Section>
          <Section
            id="results"
            title="Business Results"
            subtitle="Representative outcomes expected from this solution."
          >
            <AnimatedSection>
              <MetricGrid metrics={caseStudy.results}/>
            </AnimatedSection>
          </Section>
          <Section id="outcome" title="Business Outcome">
            <AnimatedSection>
              <div
                  className="rounded-3xl p-10 text-white"
                  style={{
                      backgroundColor: caseStudy.accent,
                  }}
              >
                {/* <h3 className="text-2xl font-bold">
                    Expected Outcome
                </h3> */}
                <p className="mt-5 text-lg leading-8 text-white/90">
                    {caseStudy.businessOutcome}
                </p>
              </div>
            </AnimatedSection>
          </Section>
          <Section
            id="technology"
            title="Technology Stack"
            subtitle="Modern technologies recommended for building a scalable, secure and future-ready solution."
          >
            <AnimatedSection>
              <div className="flex flex-wrap gap-4">

                {caseStudy.tech.map((tech) => (

                    <div
                        key={tech}
                        className="rounded-xl border border-border bg-card px-5 py-3 font-medium shadow-sm transition-all hover:-translate-y-1 hover:shadow-md"
                    >
                        {tech}
                    </div>

                ))}

              </div>
            </AnimatedSection>
          </Section>
          <Section
            id="project-info"
            title="Project Information"
            subtitle="Quick overview of the project profile."
          >
            <AnimatedSection>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">

                <HighlightCard title="Industry" description={caseStudy.industry} />

                <HighlightCard title="Category" description={caseStudy.category} />

                <HighlightCard title="Duration" description={caseStudy.duration} />

                <HighlightCard title="Services" description={`${caseStudy.services.length} Professional Services`} />
              </div>
            </AnimatedSection>
          </Section>
          <Section
            id="services"
            title="Professional Services"
            subtitle="How I can help transform this concept into a production-ready solution."
          >
            <AnimatedSection>
              <BulletGrid items={caseStudy.services} />
            </AnimatedSection>
          </Section>
          <Section id="contact" title="Let's Build Something Amazing" >
            <AnimatedSection>

              <div className="overflow-hidden rounded-3xl p-12 text-white" style={{backgroundColor: caseStudy.accent,}}>

                <div className="mx-auto max-w-3xl text-center">

                    <h2 className="font-heading text-4xl font-bold">
                        Have a Similar Business Challenge?
                    </h2>

                    <p className="mt-6 text-lg leading-8 text-white/90">
                        Whether you need a custom enterprise platform,
                        automation solution, AI-powered application,
                        or digital transformation roadmap, I'd love
                        to discuss your project.
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-4">
                        <Button variant="secondary" asChild>
                          <Link to="/contact">
                              Start Your Project
                              <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="outline" asChild>
                          <Link to="/portfolio">
                              View More Projects
                          </Link>
                        </Button>
                    </div>
                </div>
              </div>
            </AnimatedSection>
          </Section>
        </>
    );
}


// import { Link } from "react-router-dom";
// import { motion } from "framer-motion";
// import { Seo } from "@/seo/Seo";
// import { Container } from "@/components/common/Container";
// import { Section } from "@/components/common/Section";
// import { SectionHeading } from "@/components/common/SectionHeading";
// import { AnimatedHeading } from "@/components/common/AnimatedHeading";
// import { Reveal } from "@/components/common/Reveal";
// import { Counter } from "@/components/common/Counter";
// import { CtaButton } from "@/components/common/CtaButton";
// import { ConceptBadge } from "@/components/common/ConceptBadge";
// import { PillList } from "@/components/detail/PillList";
// import { TechStackChips } from "@/components/detail/TechStackChips";
// import { RelatedLinks, type RelatedLink } from "@/components/detail/RelatedLinks";
// import { DetailCta } from "@/components/detail/DetailCta";
// import { serviceDetails } from "@/data/serviceDetails";
// import { staggerContainer, fadeUp, viewportOnce, EASE } from "@/animations/motion";
// import { ArrowLeft, Clock } from "@/icons";
// import type { CaseStudy } from "@/types/content";

// function serviceLinks(slugs: string[]): RelatedLink[] {
//   return slugs
//     .map((s) => serviceDetails.find((x) => x.slug === s))
//     .filter(Boolean)
//     .map((x) => ({
//       label: x!.title,
//       description: x!.category,
//       to: `/services/${x!.slug}`,
//       icon: x!.icon,
//     }));
// }

// /** Concept dashboard mockup used in the case-study hero. */
// function HeroMockup({ accent }: { accent: string }) {
//   const bars = [48, 66, 42, 78, 60, 88, 72];
//   return (
//     <div className="overflow-hidden rounded-[1.5rem] border border-border bg-card shadow-[0_40px_100px_-40px_rgba(15,23,42,0.4)]">
//       <div className="flex items-center gap-2 px-5 py-3.5 text-white" style={{ background: accent }}>
//         <span className="h-2.5 w-2.5 rounded-full bg-white/40" />
//         <span className="h-2.5 w-2.5 rounded-full bg-white/40" />
//         <span className="h-2.5 w-2.5 rounded-full bg-white/40" />
//       </div>
//       <div className="space-y-4 p-6">
//         <div className="grid grid-cols-3 gap-3">
//           {[0, 1, 2].map((i) => (
//             <div key={i} className="rounded-xl border border-border bg-background p-3">
//               <div className="h-2 w-1/2 rounded-full bg-muted" />
//               <div className="mt-2 h-3 w-3/4 rounded-full" style={{ background: `${accent}88` }} />
//             </div>
//           ))}
//         </div>
//         <div className="rounded-xl border border-border bg-background p-5">
//           <div className="flex h-28 items-end gap-2">
//             {bars.map((h, i) => (
//               <motion.div
//                 key={i}
//                 initial={{ height: 0 }}
//                 animate={{ height: `${h}%` }}
//                 transition={{ duration: 0.6, ease: EASE, delay: 0.2 + i * 0.06 }}
//                 className="flex-1 rounded-t-md"
//                 style={{ minHeight: 6, background: `linear-gradient(to top, ${accent}55, ${accent})` }}
//               />
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export function CaseStudyTemplate({ data }: { data: CaseStudy }) {
//   const words = data.title.split(" ");
//   const titleLines =
//     words.length > 1
//       ? [words.slice(0, -1).join(" "), words.slice(-1).join(" ")]
//       : [data.title];

//   return (
//     <>
//       <Seo
//         title={`${data.title} — Case Study`}
//         description={data.summary}
//         path={`/case-studies/${data.slug}`}
//         type="article"
//       />

//       {/* Hero */}
//       <section className="relative overflow-hidden pb-14 pt-36 sm:pt-40 lg:pb-20 lg:pt-44">
//         <div className="pointer-events-none absolute inset-0 -z-10">
//           <div
//             className="absolute -top-40 left-1/2 h-[480px] w-[820px] -translate-x-1/2 rounded-full blur-[130px]"
//             style={{ background: `${data.accent}1f` }}
//           />
//           <div className="absolute inset-0 bg-grid opacity-50" />
//         </div>
//         <Container>
//           <Reveal>
//             <Link
//               to="/case-studies"
//               data-testid="case-study-back"
//               className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
//             >
//               <ArrowLeft className="h-4 w-4" /> All case studies
//             </Link>
//           </Reveal>
//           <div className="mt-6 grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
//             <div>
//               <Reveal>
//                 <div className="flex flex-wrap items-center gap-3">
//                   <ConceptBadge accent={data.accent} />
//                   <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
//                     {data.category} · {data.industry}
//                   </span>
//                 </div>
//               </Reveal>
//               <AnimatedHeading
//                 lines={titleLines}
//                 className="mt-6 max-w-2xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-[3.25rem]"
//               />
//               <Reveal delay={0.15}>
//                 <p className="mt-6 max-w-xl text-lg text-muted-foreground">{data.summary}</p>
//               </Reveal>
//               <Reveal delay={0.2}>
//                 <div className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
//                   <Clock className="h-4 w-4" /> {data.duration}
//                 </div>
//               </Reveal>
//               <Reveal delay={0.25}>
//                 <div className="mt-8">
//                   <CtaButton to="/contact" size="lg" withArrow magnetic>
//                     Start a project like this
//                   </CtaButton>
//                 </div>
//               </Reveal>
//             </div>
//             <motion.div
//               initial={{ opacity: 0, y: 24 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
//             >
//               <HeroMockup accent={data.accent} />
//             </motion.div>
//           </div>
//         </Container>
//       </section>

//       {/* Overview */}
//       <Section spacing="compact">
//         <Container>
//           <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
//             <SectionHeading eyebrow="Overview" title="The concept" />
//             <Reveal>
//               <p className="text-lg leading-relaxed text-muted-foreground">{data.overview}</p>
//             </Reveal>
//           </div>
//         </Container>
//       </Section>

//       {/* Challenge */}
//       <Section spacing="compact" className="bg-card/40">
//         <Container>
//           <SectionHeading eyebrow="The challenge" title="What we set out to solve" />
//           <div className="mt-8"><PillList items={data.challenge} columns={1} /></div>
//         </Container>
//       </Section>

//       {/* Approach */}
//       <Section spacing="compact">
//         <Container>
//           <SectionHeading
//             eyebrow="Our approach"
//             title="How we'd tackle it"
//             description="A pragmatic, low-risk path from problem to working software."
//           />
//           <motion.ol
//             variants={staggerContainer(0.08, 0.05)}
//             initial="hidden"
//             whileInView="visible"
//             viewport={viewportOnce}
//             className="mt-10 grid gap-5 sm:grid-cols-2"
//           >
//             {data.approach.map((phase, i) => (
//               <motion.li
//                 key={phase.title}
//                 variants={fadeUp}
//                 className="rounded-2xl border border-border bg-card p-6"
//               >
//                 <span
//                   className="font-mono text-sm font-semibold"
//                   style={{ color: data.accent }}
//                 >
//                   {String(i + 1).padStart(2, "0")}
//                 </span>
//                 <h3 className="mt-2 font-heading text-lg font-semibold text-foreground">
//                   {phase.title}
//                 </h3>
//                 <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
//                   {phase.description}
//                 </p>
//               </motion.li>
//             ))}
//           </motion.ol>
//         </Container>
//       </Section>

//       {/* Solution highlights */}
//       <Section spacing="compact" className="bg-card/40">
//         <Container>
//           <SectionHeading eyebrow="The solution" title="What we'd deliver" />
//           <div className="mt-8"><PillList items={data.solutionHighlights} /></div>
//         </Container>
//       </Section>

//       {/* Results */}
//       <Section spacing="compact">
//         <Container>
//           <SectionHeading
//             eyebrow="Projected impact"
//             title="Outcomes we'd aim for"
//             description="Illustrative targets for a concept build of this kind."
//           />
//           <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-[1.5rem] border border-border bg-border sm:grid-cols-3">
//             {data.results.map((r) => (
//               <div key={r.label} className="bg-card p-8 text-center">
//                 <Counter
//                   value={r.value}
//                   className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl"
//                 />
//                 <p className="mt-2 text-sm text-muted-foreground">{r.label}</p>
//               </div>
//             ))}
//           </div>
//         </Container>
//       </Section>

//       {/* Tech + related services */}
//       <Section spacing="compact" className="bg-card/40">
//         <Container>
//           <SectionHeading eyebrow="Under the hood" title="Technology" />
//           <div className="mt-8"><TechStackChips items={data.tech} /></div>
//           <h3 className="mt-14 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
//             Services involved
//           </h3>
//           <div className="mt-6"><RelatedLinks items={serviceLinks(data.services)} /></div>
//         </Container>
//       </Section>

//       <DetailCta note="Like what you see? Let's design and build the real thing together." />
//     </>
//   );
// }
